import { hasWindow, yieldToMain } from '@/utils/dom';
import { NextWebVitalsMetric } from 'next/app';
import {
	AnalyticsCssItem,
	AnalyticsEshopItem,
	AnalyticsEvent,
	ExtractEventParameters,
	GetEventNames,
} from './types';
import { country } from '@/constants/country';

type GAEvents =
	| AnalyticsEvent<'video_watched', { page_path: string }>
	| AnalyticsEvent<'reviews_open', { logged_in: 'yes' | 'no' }>
	| AnalyticsEvent<'view_modal', { modal_name: string }>
	| AnalyticsEvent<'generate_lead', { action: string }>
	| AnalyticsEvent<
			'view_promotion',
			{ search_results?: number; ecommerce: { items: AnalyticsEshopItem[] | undefined } }
	  >
	| AnalyticsEvent<'select_promotion', { ecommerce: { items: AnalyticsEshopItem[] | undefined } }>
	| AnalyticsEvent<'select_category', { content_name: string }>
	| AnalyticsEvent<
			'view_item_list',
			{ ecommerce: { item_list_id: string; items: AnalyticsCssItem[] | undefined } }
	  >
	| AnalyticsEvent<
			'select_item',
			{ ecommerce: { item_list_id: string; items: AnalyticsCssItem[] | undefined } }
	  >
	| AnalyticsEvent<
			'search',
			{ action: string; search_term?: string; search_service?: string; search_method?: string }
	  >
	| AnalyticsEvent<'select_faq', { action: string }>
	| AnalyticsEvent<'no_results', { action: string; search_term: string | undefined }>
	| AnalyticsEvent<'video', { action: string; content_id: string }>
	| AnalyticsEvent<'web_vitals', Omit<NextWebVitalsMetric, 'label'>>;

export type GAEventNames = GetEventNames<GAEvents>;

const eventCache = new Map<
	GAEventNames,
	ExtractEventParameters<GAEventNames, GAEvents> | undefined
>(null);

export function logEvent<EventName extends GAEventNames>(
	eventName: EventName,
	parameters?: ExtractEventParameters<EventName, GAEvents>
) {
	if (!hasWindow()) {
		eventCache.set(eventName, parameters);

		return;
	}

	if (!window.skippay?.pageLoaded) {
		eventCache.set(eventName, parameters);

		return;
	}
	yieldToMain(() => {
		flushCachedEvents();

		if (!window.dataLayer) {
			window.dataLayer = [];
		}

		window.dataLayer.push({
			event: eventName,
			country,
			...parameters,
		});
	});
}

export function flushCachedEvents() {
	if (eventCache.size === 0) return;

	if (!window.dataLayer) {
		window.dataLayer = [];
	}

	for (const [eventName, parameters] of eventCache) {
		window.dataLayer.push({
			event: eventName,
			country,
			...parameters,
		});
	}

	eventCache.clear();
}
